import type {
  DicomPullRequest,
  ImageUIDLookupTable,
  MetaData,
} from '@/src/components/backend/payload-typing';

import z from 'zod';

import { AnalysisSelectedSeriesSchema } from '@/src/components/backend/payload-typing';

import { MaskDataSchema } from './biomarker-mask';

type ContourType = string;
type MaskType = string;

export interface PolylineObject {
  type: 'PolylineObject';
  contourType: ContourType; // EPI, ENDO, RV, insertionPoints
  data: [number, number][] | number[];
}

export interface MaskObject {
  type: 'MaskObject';
  maskType: MaskType;
  data: [number, number][];
}

export interface SegmentationData {
  [key: string]: (PolylineObject | MaskObject)[];
}

export interface AnalysisObject {
  DicomPullRequestArray: DicomPullRequest[];
  ImageUIDLookupTable: ImageUIDLookupTable;
  SegmentationData: SegmentationData;
  MetaData: MetaData;
}

// #############################################################################

// Response Typing:

export type BiomarkerRepresentation = 'curves' | 'masks' | 'quant' | 'image';

export const BiomarkerTypeSchema = z.enum([
  'BMORE',
  'BMOREMax',
  'TemporalOCGMedian',
  'Vasoconstriction',
  'SignalIntensity',
  'siAHA16',
  'siAHA16sm',
  'EntropyDynamic',
  'LVBPSI',
  'LVBPAREA',
  'SINormToBP',
  'MinimumSI',
  'MaximumSI',
  'MedianSI',
  'Heterogeneity',
  'ocgDiaMaxIntensity',
  'ocgEntropy',
  'entroED_intensity',
  'entroSignalAmplitude',
  'energyOscillationIndex',
  'ED_p90',
  'ED_entropy',
  // OCG-V5 Quantitative Biomarkers
  'Recovery peak early-diastolic circumferential strain rate',
  'Stress peak early-diastolic circumferential strain rate',
  'Stress end-diastolic relative wall area',
  'Recovery end-diastolic relative wall area',
  'Recovery global peak circumferential strain',
  'Recovery end-systolic circumferential strain',
  'Stress peak early-diastolic area strain rate',
  'Recovery peak early-diastolic area strain rate',
  'Stress end-systolic circumferential strain',
  'Stress global peak circumferential strain',
  'Stress peak early-diastolic radial strain rate',
  'Recovery peak early-diastolic radial strain rate',
  'Recovery peak systolic circumferential strain rate',
  'Recovery radial mechanical dispersion fraction',
  'Recovery global peak area strain',
  'EF surrogate reserve',
  'Recovery end-systolic area strain',
  // OCG-V5 Regional Biomarkers (6 new maps)
  'regional_oxygenation_wall_vulnerability',
  'regional_ed_oxygenation_wall_vulnerability',
  'circumferential_strain_spectral_bandwidth',
  'image_es_nonresponse_mask',
  'image_ed_nonresponse_mask',
  'stress_to_recovery_ocg_absolute_remodeling_area',
]);
export type BiomarkerType = z.infer<typeof BiomarkerTypeSchema>;

export type BiomarkerGroupType = 'regional' | 'stress' | 'recovery' | 'other';

export const SalientIndexTypeSchema = z.enum(['ES', 'ED']);
export type SalientIndexType = z.infer<typeof SalientIndexTypeSchema>;

export const NormalizationTypeSchema = z
  .enum(['none', 'lvbp', 'edmyonorm', 'globalED'])
  .nullable()
  .optional();
export type NormalizationType
  = | z.infer<typeof NormalizationTypeSchema>
    | 'none'
    | 'globalED'
    | null;

export const OCGTerritorySchema = z.enum(['Global', 'LAD', 'RCA', 'LCx']);
export type OCGTerritory = z.infer<typeof OCGTerritorySchema>;

export const OCGCurveTypeSchema = z.enum([
  'MYOSI',
  'MYOSI_GlobalED',
  'LVBPSI',
  'LVBPAREA',
  'StressOCG',
  'RecoveryOCG',
  'StressLVArea',
  'RecoveryLVArea',
  'StressCircumferentialStrain',
  'RecoveryCircumferentialStrain',
  'StressRadialStrain',
  'RecoveryRadialStrain',
  'StressAreaStrain',
  'RecoveryAreaStrain',
]);
export type OCGCurveType = z.infer<typeof OCGCurveTypeSchema>;

export type MaskTypeBehaviour = 'Static' | 'Dynamic' | 'PolarMap';

export const OCGTerritories: OCGTerritory[] = ['Global', 'LAD', 'RCA', 'LCx'];

// 2. Structure used in response_object["BiomarkerMasks"]
export const BiomarkerPixelMaskSchema = z.object({
  biomarkerName: BiomarkerTypeSchema,
  isDynamic: z.boolean(),
  imageUID: z.string(),
  data: MaskDataSchema,
  sliceIndex: z.string(),
  refImageType: z.string().nullable(),
  range: z.tuple([z.number(), z.number()]).optional(),
});
export type BiomarkerPixelMask = z.infer<typeof BiomarkerPixelMaskSchema>;

// 3. Structure used in response_object["BiomarkerCurves"]
export const BiomarkerOCGCurveTypeSchema = z.object({
  biomarkerName: OCGCurveTypeSchema,
  territory: OCGTerritorySchema,
  normalization: NormalizationTypeSchema,
  data: z.array(z.number().nullable()), // Nullable for NaNs
  sliceIndex: z.string(),
  interpolatedIndexes: z.array(z.number()).nullable(), // Optional array of indexes where interpolation was applied, used for viewer indicators
  isCycleNormalized: z.boolean().nullable().optional(),
  cycleType: z.string().nullable().optional(),
  startFrame: z.number().nullable().optional(),
  endFrame: z.number().nullable().optional(),
  esFrame: z.number().nullable().optional(),
  esPhaseIndex: z.number().nullable().optional(),
  edPhaseIndexes: z.array(z.number()).nullable().optional(),
});
export type BiomarkerOCGCurveType = z.infer<typeof BiomarkerOCGCurveTypeSchema>;

// 4. Structure used in response_object["BiomarkerQuantitativeData"]
export const BiomarkerQuantitativeDataTypeSchema = z.object({
  biomarkerName: z.union([BiomarkerTypeSchema, z.string()]),
  data: z.union([
    z.array(z.number().nullable()),
    z.array(z.array(z.number().nullable())),
  ]),
  sliceIndex: z.string(),
});
export type BiomarkerQuantitativeDataType = z.infer<
  typeof BiomarkerQuantitativeDataTypeSchema
>;

// 5. Structure used in response_object["StaticImages"]
export interface StaticImageType {
  sliceIndex: string;
  imageUID: string;
  data: number[][]; // 2D image matrix
  shape: [number, number]; // [height, width]
  type: string; // e.g., "BMORE", "Vasoconstriction"
}

// 6. Metadata Sub-structures
export const SalientIndexItemSchema = z.object({
  type: SalientIndexTypeSchema,
  data: z.array(z.number()),
  sliceIndex: z.string(),
});
export type SalientIndexItem = z.infer<typeof SalientIndexItemSchema>;

export const InterpolatedImageIndexItemSchema = z.object({
  sliceIndex: z.string(),
  imageUID: z.string(),
  index: z.number(),
});
export type InterpolatedImageIndexItem = z.infer<
  typeof InterpolatedImageIndexItemSchema
>;

export const MetadataSchema = z.object({
  interpolatedImageIndexes: z.array(InterpolatedImageIndexItemSchema),
  selectedSeries: z.array(AnalysisSelectedSeriesSchema),
});
export type Metadata = z.infer<typeof MetadataSchema>;

// 7. Structure used in response_object["BiomarkerAHAData"]
export const BiomarkerAHADataTypeSchema = z.object({
  biomarkerName: BiomarkerTypeSchema,
  data: z.union([
    z.array(z.number().nullable()),
    z.array(z.array(z.number().nullable())), // Exception for siAHA16sm, which returns a 2D array of shape [N, 6] where N is the phases and 6 is the AHA segments. Deprecated and will be removed.
  ]),
  sliceIndex: z.string(),
  range: z.tuple([z.number(), z.number()]).nullable().optional(),
});
export type BiomarkerAHADataType = z.infer<typeof BiomarkerAHADataTypeSchema>;

export const LabelProbabilityDataSchema = z.object({
  sliceIndex: z.string(),
  data: z.record(z.string(), z.number()),
});
export type LabelProbabilityData = z.infer<typeof LabelProbabilityDataSchema>;

// // --- ROOT TYPE ---
// export interface AnalysisResult {
//   BiomarkerMasks: BiomarkerPixelMask[];
//   BiomarkerCurves: BiomarkerOCGCurveType[];
//   BiomarkerQuantitativeData: BiomarkerQuantitativeDataType[];
//   BiomarkerAHAData: BiomarkerAHADataType[];
//   SalientIndexes: SalientIndexItem[];
//   // StaticImages: StaticImageType[];
//   LabelProbabilities?: LabelProbabilityData[];
//   Metadata: Metadata;
// }

export const AnalysisResultSchema = z.object({
  BiomarkerMasks: z.array(BiomarkerPixelMaskSchema),
  BiomarkerCurves: z.array(BiomarkerOCGCurveTypeSchema),
  BiomarkerQuantitativeData: z.array(BiomarkerQuantitativeDataTypeSchema),
  BiomarkerAHAData: z.array(BiomarkerAHADataTypeSchema),
  SalientIndexes: z.array(SalientIndexItemSchema),
  // StaticImages: StaticImageType[];
  LabelProbabilities: z.array(LabelProbabilityDataSchema).optional(),
  Metadata: MetadataSchema,
});

export type AnalysisResult = z.infer<typeof AnalysisResultSchema>;
